import { LowLevelPaymentMethod } from "interface/LowLevelPaymentMethod";
import { DefaultApiResponse } from "../models/response/DefaultApiResponse";
import { fetchApiData } from "../ApiHelper";
import { get, post } from "../ApiClient";
import EndpointProps from "interface/EndpointProps";
import { LegacyPaymentProps } from "interface/LegacyPaymentProps";

/**
 * Fetches the default payment method.
 *
 * @param {Object} params - The parameters for fetching the default payment method.
 * @param {string} params.tag - The tag associated with the endpoint.
 * @returns {Promise<{ success: boolean; data: LowLevelPaymentMethod | null; error: string | null }>} A promise that resolves to an object containing the response data or an error message.
 *
 * @throws {Error} Throws an error if the fetch operation fails.
 */
export async function fetchDefaultPaymentMethod({ tag }: EndpointProps): Promise<{ success: boolean; data: LowLevelPaymentMethod | null; error: string | null }> {
    const result = await fetchApiData<DefaultApiResponse<LowLevelPaymentMethod>>(
        tag,
        () => get<DefaultApiResponse<LowLevelPaymentMethod>>({ tag, url: "/payment/method/default" }),
        (responseData) => responseData.data ?? null
    )

    return {
        success: result.response.status === "success",
        data: result.response.status === "success" ? result.response.data : null,
        error: result.response.status === "error" ? result.response.message : null
    }
}

/**
 * Fetches all card payment methods.
 * 
 * @param {Object} params - The parameters for fetching all card payment methods.
 * @param {string} params.tag - The tag associated with the endpoint.
 * 
 * @returns {Promise<{ success: boolean; data: LowLevelPaymentMethod[] | null; error: string | null }>} A promise that resolves to an object containing the response data or an error message.
 * 
 * @throws {Error} Throws an error if the fetch operation fails.
 */
export async function fetchAllCardPaymentMethods({ tag }: EndpointProps): Promise<{ success: boolean; data: LowLevelPaymentMethod[] | null; error: string | null }> {
    const result = await fetchApiData<DefaultApiResponse<LowLevelPaymentMethod[]>>(
        tag,
        () => get<DefaultApiResponse<LowLevelPaymentMethod[]>>({ tag, url: "/payment/method/cards" }),
        (responseData) => responseData.data ?? null
    )

    return {
        success: result.response.status === "success",
        data: result.response.status === "success" ? result.response.data : null,
        error: result.response.status === "error" ? result.response.message : null
    }
}

/**
 * Processes a legacy subscription payment.
 * @param {Object} params - The parameters for processing the legacy subscription payment.
 * @param {string} params.tag - The tag associated with the endpoint.
 * @param {LegacyPaymentProps} params.data - The data payload for the legacy payment processing.
 */
export async function processLegacySubscriptionPayment({ tag, data }: EndpointProps & { data: LegacyPaymentProps }): Promise<{ success: boolean; error?: string | null }> {
    const result = await fetchApiData<DefaultApiResponse>(
        tag,
        () => post<DefaultApiResponse>({ tag, url: `/subscription/payment/legacy/update`, body: data }),
        (responseData) => responseData
    )

    return {
        success: result.response.status === "success",
        error: result.response.status === "error" ? result.response.message : null
    }
}

/**
 * Sets a payment method as the default.
 * 
 * @param {Object} params - The parameters for setting the default payment method.
 * @param {string} params.tag - The tag associated with the endpoint.
 * @param {Object} params.data - The data payload containing the payment method ID to be set as default.
 * @param {string} params.data.paymentMethodId - The ID of the payment method to be set as default.
 * 
 * @returns {Promise<{ success: boolean; error?: string | null }>} A promise that resolves to an object indicating the success status and any error message.
 * 
 * @throws {Error} Throws an error if the operation fails.
 * 
 * @example
 * const response = await setDefaultPaymentMethod({ tag: 'SetDefaultPM', data: { paymentMethodId: 'pm_123456789' } });
 * if (response.success) {
 *   console.log('Payment method set as default successfully.');
 * } else {
 *  console.error('Failed to set payment method as default:', response.error);
 * }
 **/
export async function detachPaymentMethod({ tag, data }: EndpointProps & { data: { paymentMethodId: string } }): Promise<{ success: boolean; error?: string | null }> {
    const result = await fetchApiData<DefaultApiResponse>(
        tag,
        () => post<DefaultApiResponse>({ tag, url: `/payment/method/detach`, body: data }),
        (responseData) => responseData
    )

    return {
        success: result.response.status === "success",
        error: result.response.status === "error" ? result.response.message : null
    }
}

/** Sets a payment method as the default.
 * 
 * @param {Object} params - The parameters for setting the default payment method.
 * @param {string} params.tag - The tag associated with the endpoint.
 * @param {Object} params.data - The data payload containing the payment method ID to be set as default.
 * 
 * @return {Promise<{ success: boolean; error?: string | null }>} A promise that resolves to an object indicating the success status and any error message.
 * 
 * @throws {Error} Throws an error if the operation fails.
 * 
 * @example
 * const result = await setDefaultPaymentMethod({ tag: 'SetDefault', data: { paymentMethodId: 'pm_1Hxxxxxx' } });
 * if (result.success) {
 *   console.log('Payment method set as default successfully.');
 * } else {
 *  console.error('Failed to set payment method as default:', result.error);
 * }
 */
export async function setDefaultPaymentMethod({ tag, data }: EndpointProps & { data: { paymentMethodId: string } }): Promise<{ success: boolean; error?: string | null }> {
    const result = await fetchApiData<DefaultApiResponse>(
        tag,
        () => post<DefaultApiResponse>({ tag, url: `/payment/method/set-default`, body: data }),
        (responseData) => responseData
    )

    return {
        success: result.response.status === "success",
        error: result.response.status === "error" ? result.response.message : null
    }
}

/**
 * Creates a new payment method setup intent and retrieves the client secret.
 * 
 * @param {Object} params - The parameters for creating a new payment method setup intent.
 * @param {string} params.tag - The tag associated with the endpoint.
 * 
 * @returns {Promise<{ success: boolean; clientSecret?: string | null; error?: string | null }>} A promise that resolves to an object containing the success status, client secret if successful, or an error message if failed.
 * 
 * @throws {Error} Throws an error if the operation fails.
 * 
 * @example
 * const response = await newPaymentMethodSetup({ tag: 'NewPMSetup' });
 * if (response.success) {
 *   console.log('Client secret for new payment method setup:', response.clientSecret);
 * } else {
 *  console.error('Failed to create new payment method setup:', response.error);
 * } 
 */
export async function newPaymentMethodSetup({ tag }: EndpointProps): Promise<{ success: boolean; clientSecret?: string | null; error?: string | null }> {
    const result = await fetchApiData<DefaultApiResponse<{ clientSecret: string }>>(
        tag,
        () => get<DefaultApiResponse<{ clientSecret: string }>>({ tag, url: `/payment/method/setup` }),
        (responseData) => responseData.data ?? null
    )

    return {
        success: result.response.status === "success",
        clientSecret: result.response.status === "success" ? result.response.data.clientSecret : null,
        error: result.response.status === "error" ? result.response.message : null
    }
}